import html2canvas, { Options as Html2CanvasOptions } from "html2canvas";
import { message } from "antd";

export const convertHtmlToCanvas = async ({
  element,
  loadingMessage = "Generando imagen...",
  onLoading = null,
  canvasOptions = {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false
  },

}: any) => {
  if (!element) return null;

  let closeLoading;

  try {
    if (onLoading) {
      closeLoading = onLoading(loadingMessage);
    }

    const canvas = await html2canvas(element,);

    return canvas;
  } catch (error) {
    console.error("Error converting html to image:", error);
    return null;
  }
  finally {
    closeLoading?.();
  }
};

export const downloadImage = async ({
  element,
  fileName = "image",
  fileType = "png",
  onSuccess,
  onError,
}: any) => {
  try {

    const canvas = await convertHtmlToCanvas({
      element: element,
      onLoading: (msg: string) => message.loading(msg, 0),
    });

    if (!canvas) return;

    const mimeType = `image/${fileType}`;
    const image = canvas.toDataURL(mimeType);

    const link = document.createElement("a");
    link.download = `${fileName}.${fileType}`;
    link.href = image;
    link.click();

    onSuccess?.("Imagen descargada correctamente");
  } catch (error) {
    console.error("Error downloading image:", error);
    onError?.("Error al descargar la imagen");
  }
};

export const formatDecimals = (value: number | string, decimals: number = 2, locale = undefined, alwaysPositive: boolean = false) => {
  let num = Number(value ?? 0);

  if (isNaN(num)) num = 0;

  if (alwaysPositive) num = Math.abs(num);

  return num.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};


export const numberToLetters = (num: number): string => {
  num = Math.floor(num);

  if (num < 0) {
    return `menos ${numberToLetters(Math.abs(num))}`;
  }

  const units = [
    "", "uno", "dos", "tres", "cuatro", "cinco",
    "seis", "siete", "ocho", "nueve"
  ];

  const specials = [
    "diez", "once", "doce", "trece", "catorce", "quince",
    "dieciséis", "diecisiete", "dieciocho", "diecinueve"
  ];

  const tens = [
    "", "", "veinte", "treinta", "cuarenta",
    "cincuenta", "sesenta", "setenta",
    "ochenta", "noventa"
  ];

  const hundreds = [
    "", "ciento", "doscientos", "trescientos",
    "cuatrocientos", "quinientos",
    "seiscientos", "setecientos",
    "ochocientos", "novecientos"
  ];

  if (num < 10) return units[num];

  if (num < 20) return specials[num - 10];

  if (num < 100) {
    const d = Math.floor(num / 10);
    const u = num % 10;

    if (u === 0) return tens[d];
    if (d === 2) return `veinti${units[u]}`;

    return `${tens[d]} y ${units[u]}`;
  }

  if (num === 100) return "cien";

  if (num < 1000) {
    const c = Math.floor(num / 100);
    const r = num % 100;

    if (r === 0) return hundreds[c];

    return `${hundreds[c]} ${numberToLetters(r)}`;
  }

  if (num < 1000000) {
    const thousands = Math.floor(num / 1000);
    const r = num % 1000;

    let thousandsText =
      thousands === 1
        ? "mil"
        : `${numberToLetters(thousands)} mil`;

    if (r === 0) return thousandsText;

    return `${thousandsText} ${numberToLetters(r)}`;
  }

  if (num < 1000000000) {
    const millions = Math.floor(num / 1000000);
    const r = num % 1000000;

    let millionsText =
      millions === 1
        ? "un millón"
        : `${numberToLetters(millions)} millones`;

    if (r === 0) return millionsText;

    return `${millionsText} ${numberToLetters(r)}`;
  }

  return num.toString();
};

